// simulation.js - The single, deterministic game step shared by BOTH the live
// game (App.js) and the run validator (validator.js). Keeping one copy means the
// two can never diverge, which is what makes replay-based anti-cheat trustworthy.
//
// Rules for everything in here:
//   - No DOM, no React, no canvas, no audio. Side-effects the live layer needs
//     (sfx, particles on death) are emitted as `game.events` for it to consume.
//   - All randomness that affects the outcome goes through game.rng (seeded).
//     Cosmetic randomness (stars, particle offsets) may use Math.random since it
//     never influences score/collisions/spawns.
//   - All timing is in ticks (60/sec), never Date.now, so a run is reproducible.

import {
  updatePlayerMovement,
  checkPlatformCollisions,
  checkCoinCollisions,
  checkBoostCollisions,
  checkEnemyCollisions,
  updateProjectiles,
  updateMovingPlatforms,
  updateMovingEnemies,
  updateCoinRotation,
  updateMagnetEffect,
  updateSpringAnimations,
  shootProjectile,
} from './physics';
import { createPlatform, createCoin, createBoost, createEnemy } from './spawning';
import { updateParticles } from './particles';

// Boost/animation durations in ticks (seconds * 60).
export const TICKS = {
  rocket: 180, // 3s
  slowmo: 300, // 5s
  magnet: 300, // 5s
  double: 480, // 8s
  shield: 600, // 10s
  death: 60,   // 1s enemy-death freeze (presentation only)
};
export const SHOOT_COOLDOWN_TICKS = 9; // auto-fire cadence while the shoot key is held

// Pure boost activation (no audio/React). Emits a 'boost' event so the live
// layer can play the right pickup sound.
export const activateBoostState = (game, type) => {
  const t = game.animationFrame;

  if (type === 'rocket') {
    game.coinsForRocket = Math.max(0, game.coinsForRocket - 10);
    game.activeBoost = type;
    game.boostEndTick = t + TICKS.rocket;
    game.rocketActive = true;
    game.rocketEndY = game.player.y - 500;
    game.rocketEndTick = t + TICKS.rocket;
    game.player.vy = -12;
  } else if (type === 'shield') {
    game.shieldActive = true;
    game.shieldEndTick = t + TICKS.shield;
  } else if (type === 'shooter') {
    game.shootingEnabled = true;
    if (!game.devMode) {
      game.bulletsRemaining = Math.min(game.bulletsRemaining + 10, game.maxBullets);
    }
  } else if (type === 'slowmo') {
    game.speedModifier = 0.5;
    game.speedModEndTick = t + TICKS.slowmo;
    game.activeBoost = type;
    game.boostEndTick = t + TICKS.slowmo;
  } else if (type === 'magnet') {
    game.magnetActive = true;
    game.magnetEndTick = t + TICKS.magnet;
    game.activeBoost = type;
    game.boostEndTick = t + TICKS.magnet;
  } else {
    game.activeBoost = type;
    game.boostEndTick = t + TICKS.double;
  }

  game.events.push({ k: 'boost', type });
};

// Advance the simulation by exactly one tick. Mutates `game`. Sets game.gameOver
// (+ reason) when the run ends. Assumes input has already been applied to
// game.keys / game.shootPulse for this tick.
export const stepSimulation = (game, W, H) => {
  game.events = [];
  const p = game.player;
  game.animationFrame++;
  const t = game.animationFrame;

  // Boost collection (incl. shooting boosts) routes through the pure activator.
  game.boostCollisionCallback = (type) => activateBoostState(game, type);

  // Tick-based timer expiry.
  if (game.rocketActive && t >= game.rocketEndTick) game.rocketActive = false;
  if (game.activeBoost && t >= game.boostEndTick) game.activeBoost = null;
  if (game.shieldActive && t >= game.shieldEndTick) game.shieldActive = false;
  if (game.speedModifier !== 1.0 && t >= game.speedModEndTick) game.speedModifier = 1.0;
  if (game.magnetActive && t >= game.magnetEndTick) game.magnetActive = false;

  updatePlayerMovement(game, W);

  // Deterministic shooting: auto-fire on a fixed cadence while held, plus a
  // one-shot pulse (touch tap). No event-driven firing, so it replays exactly.
  if (game.shootCooldown > 0) game.shootCooldown--;
  const wantShoot = !!game.keys['__shoot'] || game.shootPulse;
  game.shootPulse = false;
  if (wantShoot && game.shootCooldown <= 0 &&
      (game.devMode || (game.shootingEnabled && game.bulletsRemaining > 0))) {
    shootProjectile(game);
    game.shootCooldown = SHOOT_COOLDOWN_TICKS;
  }

  checkPlatformCollisions(game);
  checkCoinCollisions(game);
  checkBoostCollisions(game, game.boostCollisionCallback);
  updateProjectiles(game);

  if (checkEnemyCollisions(game)) {
    game.gameOver = true;
    game.gameOverReason = 'enemy';
    game.events.push({ k: 'dead', reason: 'enemy' });
    return;
  }

  // Camera follows the player up; everything scrolls down and we top up spawns.
  if (p.y < H / 2) {
    const diff = H / 2 - p.y;
    game.cameraY -= diff;
    game.backgroundOffset += diff * 0.3;
    p.y = H / 2;

    const meters = Math.floor(-game.cameraY / 10);
    game.score = Math.max(game.score, meters);

    game.platforms.forEach(plat => { plat.y += diff; });
    game.coins.forEach(coin => { coin.y += diff; });
    game.boosts.forEach(boost => { boost.y += diff; });
    game.enemies.forEach(enemy => {
      enemy.y += diff;
      if (enemy.baseY !== undefined) enemy.baseY += diff;
    });
    game.projectiles.forEach(proj => { proj.y += diff; });
    game.particles.forEach(pt => { pt.y += diff; });

    // Stars are cosmetic (Math.random, not game.rng — never affects outcome).
    game.stars.forEach(star => {
      star.y += diff * 0.3;
      if (star.y > H) {
        star.y = -Math.random() * 50 - 10;
        star.x = Math.random() * W;
      }
    });
    if (Math.random() > 0.95 && game.stars.length < 80) {
      game.stars.push({
        x: Math.random() * W,
        y: -Math.random() * 100,
        size: Math.random() * 2 + 0.5,
        brightness: Math.random() * 0.7 + 0.3,
      });
    }

    game.platforms = game.platforms.filter(plat => plat.y < H + 100 && plat.y > -500);
    game.coins = game.coins.filter(coin => coin.y < H + 100);
    game.boosts = game.boosts.filter(boost => boost.y < H + 200);
    game.enemies = game.enemies.filter(enemy => enemy.y < H + 200);
    game.projectiles = game.projectiles.filter(proj => proj.y < H + 100);

    while (game.platforms.length < 15) {
      const lastY = Math.min(...game.platforms.map(pl => pl.y));

      let newY = lastY - game.rng() * 30 - 50;
      if (Math.abs(newY - lastY) > 70) newY = lastY - 70;

      const currentHeight = Math.floor(-game.cameraY / 10);

      let platformDensity;
      if (currentHeight < 100) platformDensity = 0.98;
      else if (currentHeight < 300) platformDensity = 0.90;
      else if (currentHeight < 600) platformDensity = 0.82;
      else platformDensity = 0.70;

      if (game.rng() > platformDensity && game.platforms.filter(pl => pl.y < lastY + 160).length > 2) {
        continue;
      }

      const unsafePlatforms = game.platforms.filter(pl => pl.y < lastY + 80);
      const lastWasUnsafe = unsafePlatforms.some(pl =>
        (pl.type === 'breakable' || pl.type === 'fragile') && !pl.broken
      );

      const newPlat = createPlatform(newY, W, lastWasUnsafe, currentHeight, game.rng);

      const recentPlatforms = game.platforms.filter(pl => pl.y < newY + 150 && pl.y > newY - 150);
      let validPosition = false;
      let attempts = 0;
      while (!validPosition && attempts < 30) {
        validPosition = true;
        for (const existing of recentPlatforms) {
          const horizontalGap = Math.abs((newPlat.x + newPlat.w / 2) - (existing.x + existing.w / 2));
          const verticalGap = Math.abs(newPlat.y - existing.y);
          if (horizontalGap < (newPlat.w + existing.w) / 2 + 50) {
            validPosition = false;
            newPlat.x = game.rng() * (W - newPlat.w);
            break;
          }
          if (verticalGap < 40) {
            validPosition = false;
            newPlat.x = game.rng() * (W - newPlat.w);
            break;
          }
        }
        attempts++;
      }

      game.platforms.push(newPlat);

      if (newPlat.type === 'static' && game.rng() > 0.92) {
        const nearbySpring = game.platforms.some(pl =>
          pl.hasSpring && Math.abs(pl.y - newPlat.y) < 200 && !pl.broken
        );
        if (!nearbySpring) newPlat.hasSpring = true;
      }

      if (game.rng() > 0.6) {
        const newCoin = createCoin(newY, W, game.rng);
        const recentCoins = game.coins.filter(c => !c.collected && c.y < newY + 150 && c.y > newY - 150);
        let tooClose = false;
        for (const existingCoin of recentCoins) {
          const dx = newCoin.x - existingCoin.x;
          const dy = newCoin.y - existingCoin.y;
          if (Math.sqrt(dx * dx + dy * dy) < 80) { tooClose = true; break; }
        }
        if (!tooClose) {
          game.coins.push(newCoin);
          game.coinsSpawnedTotal += 1;
        }
      }

      if (game.options.boostsEnabled) {
        const boostChance = (game.devMode && currentHeight < 200) ? 0.4 : 0.92;
        if (game.rng() > boostChance) {
          const boost = createBoost(newY, W, game.rng);
          if (boost.type === 'rocket' && !game.devMode && game.coinsForRocket < 10) {
            boost.type = 'doublePoints';
          }
          const overlapsWithPlatform = game.platforms.some(plat => {
            const horizontalOverlap = boost.x < plat.x + plat.w && boost.x + boost.w > plat.x;
            const verticalOverlap = Math.abs(boost.y - plat.y) < 50;
            return horizontalOverlap && verticalOverlap;
          });
          const boostAlreadyExists = game.boosts.some(b => !b.collected && b.type === boost.type);
          const overlapsWithBoost = game.boosts.some(b => {
            if (b.collected) return false;
            const dx = Math.abs((boost.x + boost.w / 2) - (b.x + b.w / 2));
            const dy = Math.abs(boost.y - b.y);
            return dx < 50 && dy < 50;
          });
          if (!boostAlreadyExists && !overlapsWithPlatform && !overlapsWithBoost) {
            game.boosts.push(boost);
          }
        }
      }

      if (game.options.enemiesEnabled) {
        let enemyChance;
        if (currentHeight < 50) enemyChance = 1.0;
        else if (currentHeight < 150) enemyChance = 0.97;
        else if (currentHeight < 300) enemyChance = 0.93;
        else if (currentHeight < 600) enemyChance = 0.88;
        else enemyChance = 0.82;

        const recentEnemies = game.enemies.filter(e => e.y < newY + 300 && e.y > newY - 100);
        if (recentEnemies.length < 2 && game.rng() > enemyChance) {
          game.enemies.push(createEnemy(newY, W, game.rng));
        }
      }
    }
  }

  updateMovingPlatforms(game, W);
  updateMovingEnemies(game, W);
  updateCoinRotation(game);
  updateMagnetEffect(game);
  updateSpringAnimations(game);
  updateParticles(game);

  if (p.y > H + 5) {
    game.gameOver = true;
    game.gameOverReason = 'fall';
    game.events.push({ k: 'dead', reason: 'fall' });
  }
};
